import * as XLSX from "xlsx";

const DEFAULT_IMPORT_ROLE = "owner";

export const ROW_STATUS = {
  READY: "ready",
  INVALID: "invalid",
  SKIPPED: "skipped",
  CREATED: "created",
  FAILED: "failed",
};

const normalizeHeader = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[\u2018\u2019\u201a\u201b\u2032\u2035`´']/g, "'")
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const HEADER_RULES = {
  email: {
    aliases: ["email", "adresse mail", "adresse email", "e mail", "mail"],
    keywords: ["email", "mail"],
    exclude: ["telephone", "phone", "mot de passe", "password"],
  },
  firstName: {
    aliases: ["firstname", "first name", "prenom", "prénom"],
    keywords: ["prenom", "prénom", "firstname", "first name"],
    exclude: ["nom", "name", "last"],
  },
  lastName: {
    aliases: ["lastname", "last name", "nom"],
    keywords: ["nom", "lastname", "last name", "name"],
    exclude: ["prenom", "prénom", "first", "producteur"],
  },
  phone: {
    aliases: ["phone", "telephone", "téléphone", "tel"],
    keywords: ["telephone", "téléphone", "phone", "tel"],
    exclude: ["mail", "email"],
  },
  password: {
    aliases: ["password", "mot de passe", "mdp"],
    keywords: ["password", "mot de passe", "mdp"],
    exclude: ["confirmation", "confirm"],
  },
};

const matchesHeaderRule = (normalizedHeader, rule) => {
  if (!normalizedHeader) return false;
  if (rule.exclude?.some((term) => normalizedHeader.includes(term))) {
    return false;
  }
  if (rule.aliases.includes(normalizedHeader)) return true;
  return rule.keywords.some((keyword) => normalizedHeader.includes(keyword));
};

const resolveHeaderMap = (headers = []) => {
  const normalizedHeaders = headers.map((header) => normalizeHeader(header));
  const map = {};
  const usedIndices = new Set();

  ["email", "firstName", "lastName", "phone", "password"].forEach(
    (targetKey) => {
      const rule = HEADER_RULES[targetKey];
      const index = normalizedHeaders.findIndex(
        (header, headerIndex) =>
          !usedIndices.has(headerIndex) && matchesHeaderRule(header, rule),
      );
      if (index >= 0) {
        map[targetKey] = headers[index];
        usedIndices.add(index);
      }
    },
  );

  return map;
};

const normalizeValue = (value) => String(value ?? "").trim();

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const parseUserImportFile = async (file) => {
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data, { type: "array" });
  const firstSheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[firstSheetName];
  const jsonRows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
  const headers = jsonRows.length > 0 ? Object.keys(jsonRows[0]) : [];
  const headerMap = resolveHeaderMap(headers);

  if (!headerMap.email) {
    throw new Error("missing-columns");
  }

  const mappedRows = jsonRows.map((row, index) => ({
    line: index + 2,
    email: headerMap.email
      ? String(row[headerMap.email] || "")
          .trim()
          .replace(/\s/g, "")
          .toLowerCase()
      : "",
    firstName: headerMap.firstName
      ? String(row[headerMap.firstName] || "").trim()
      : "",
    lastName: headerMap.lastName
      ? String(row[headerMap.lastName] || "").trim()
      : "",
    phone: headerMap.phone ? String(row[headerMap.phone] || "").trim() : "",
    password: headerMap.password
      ? String(row[headerMap.password] || "").trim()
      : "",
    role: DEFAULT_IMPORT_ROLE,
  }));

  const rows = mappedRows.filter(
    (row) =>
      row.email || row.firstName || row.lastName || row.phone || row.password,
  );

  return { rows, headerMap };
};

export const validateImportRows = (
  rows,
  existingUsers = [],
  { googleSSORequired = false } = {},
) => {
  const existingEmails = new Set(
    existingUsers
      .map((user) => normalizeValue(user.email).toLowerCase())
      .filter(Boolean),
  );

  const seenEmails = new Set();

  return rows.map((row) => {
    const email = normalizeValue(row.email).toLowerCase();
    const firstName = normalizeValue(row.firstName);
    const lastName = normalizeValue(row.lastName);
    const phone = normalizeValue(row.phone);
    const password = normalizeValue(row.password);
    const role = DEFAULT_IMPORT_ROLE;
    const issueCodes = [];

    if (!email) {
      return {
        ...row,
        email,
        firstName,
        lastName,
        phone,
        password,
        role,
        status: ROW_STATUS.INVALID,
        issueCodes: ["missing_required"],
      };
    }

    if (!isValidEmail(email)) {
      return {
        ...row,
        email,
        firstName,
        lastName,
        phone,
        password,
        role,
        status: ROW_STATUS.INVALID,
        issueCodes: ["invalid_email"],
      };
    }

    if (!googleSSORequired) {
      if (!password) {
        return {
          ...row,
          email,
          firstName,
          lastName,
          phone,
          password,
          role,
          status: ROW_STATUS.INVALID,
          issueCodes: ["missing_password"],
        };
      }
      if (password.length < 6) {
        return {
          ...row,
          email,
          firstName,
          lastName,
          phone,
          password,
          role,
          status: ROW_STATUS.INVALID,
          issueCodes: ["invalid_password"],
        };
      }
    }

    let status = ROW_STATUS.READY;

    if (existingEmails.has(email) || seenEmails.has(email)) {
      status = ROW_STATUS.SKIPPED;
      issueCodes.push("duplicate_email");
    }

    seenEmails.add(email);

    return {
      ...row,
      email,
      firstName,
      lastName,
      phone,
      password,
      role,
      status,
      issueCodes,
    };
  });
};

const lineKey = (line) => Number(line);

export const mergeImportReport = (rows, report) => {
  if (!report) return rows;

  const statusByLine = new Map();
  (report.results || []).forEach((item) => {
    statusByLine.set(lineKey(item.line), item);
  });
  (report.failed || []).forEach((item) => {
    const key = lineKey(item.line);
    if (!statusByLine.has(key)) statusByLine.set(key, item);
  });
  (report.skipped || []).forEach((item) => {
    const key = lineKey(item.line);
    if (!statusByLine.has(key)) statusByLine.set(key, item);
  });

  return rows.map((row) => {
    const result = statusByLine.get(lineKey(row.line));
    if (!result) {
      return row;
    }

    if (result.status === "created") {
      return { ...row, status: ROW_STATUS.CREATED, issueCodes: [] };
    }
    if (result.status === "skipped" || result.code?.startsWith("duplicate")) {
      return {
        ...row,
        status: ROW_STATUS.SKIPPED,
        issueCodes: [result.code || "duplicate_email"],
      };
    }
    return {
      ...row,
      status: ROW_STATUS.FAILED,
      issueCodes: result.missingFields
        ? ["missing_required"]
        : [result.code || "failed"],
    };
  });
};

export const countRowsByStatus = (rows) =>
  rows.reduce((acc, row) => {
    acc[row.status] = (acc[row.status] || 0) + 1;
    return acc;
  }, {});

export const getRowsToImport = (rows) =>
  rows.filter((row) => row.status === ROW_STATUS.READY);
